import type { FileFormatScope } from '@steerfi/shared';
import type {
  FileFormatMapping,
  FileFormatMetadata,
  FileFormatSchema,
  FileFormatValidationRules,
} from '@steerfi/shared/types/jsonb/file-format.types';
import keycloakApi from './keycloak-auth';

interface FileFormatPaginationData {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface FileFormatPartySummary {
  display_name: string | null;
  client_code: string | null;
  party_role_id?: number;
}

export interface FileFormatApiRow {
  id: number;
  party_id: string | null;
  file_type_id: number;
  format_name: string;
  code: string;
  description: string | null;
  version_number: number;
  s3_bucket_name: string | null;
  metadata_json: FileFormatMetadata | null;
  schema_json: FileFormatSchema | null;
  mapping_json: FileFormatMapping | null;
  validation_rules_json: FileFormatValidationRules | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  party?: FileFormatPartySummary | null;
}

/** Minimal row shape for dropdowns (e.g. integration profile file format picker). */
export type FileFormatRow = Pick<FileFormatApiRow, 'id' | 'code' | 'format_name'>;

export type FileFormatListSortBy =
  | 'format_name'
  | 'code'
  | 'file_type_id'
  | 'version_number'
  | 'created_at'
  | 'updated_at';

export type FileFormatStatusFilter = 'active' | 'deleted';

export interface FileFormatListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: FileFormatStatusFilter | FileFormatStatusFilter[];
  sortBy?: FileFormatListSortBy;
  order?: 'asc' | 'desc';
  scope?: FileFormatScope;
  party_id?: string;
}

interface ListFileFormatsResult {
  data: FileFormatApiRow[];
  pagination: FileFormatPaginationData;
}

export interface CreateFileFormatPayload {
  format_name: string;
  code: string;
  file_type_id: number;
  party_id?: string | null;
  description?: string | null;
  s3_bucket_name?: string | null;
  version_number?: number;
  metadata_json?: FileFormatMetadata | null;
  schema_json?: FileFormatSchema | null;
  mapping_json?: FileFormatMapping | null;
  validation_rules_json?: FileFormatValidationRules | null;
}

export type UpdateFileFormatPayload = Partial<Omit<CreateFileFormatPayload, 'party_id'>>;

const API_BASE_URL = '/internal/file-formats';

const apiCall = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  return keycloakApi.authenticatedApiCall<T>(`${API_BASE_URL}${endpoint}`, options);
};

const buildQuery = (params: FileFormatListQuery): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      for (const item of value) {
        searchParams.append(key, String(item));
      }
      return;
    }
    searchParams.append(key, String(value));
  });
  const s = searchParams.toString();
  return s ? `?${s}` : '';
};

// Read-side calls accept an optional AbortSignal so consumers (notably
// useFileFormats) can cancel in-flight requests when the user types
// faster than the network. `RequestInit.signal` flows through the
// keycloak-auth wrapper to the underlying fetch, so no shared-layer
// changes are required.
interface FetchOptions {
  signal?: AbortSignal;
}

export const listFileFormats = (q: FileFormatListQuery = {}, opts: FetchOptions = {}) =>
  apiCall<ListFileFormatsResult>(buildQuery(q), opts);

export const getFileFormat = (id: number, opts: FetchOptions = {}) =>
  apiCall<FileFormatApiRow>(`/${id}`, opts);

export const createFileFormat = (data: CreateFileFormatPayload) =>
  apiCall<FileFormatApiRow>('/', { method: 'POST', body: JSON.stringify(data) });

export const updateFileFormat = (id: number, data: UpdateFileFormatPayload) =>
  apiCall<FileFormatApiRow>(`/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteFileFormat = (id: number) =>
  apiCall<{ success: boolean; message: string }>(`/${id}`, { method: 'DELETE' });

export const restoreFileFormat = (id: number) =>
  apiCall<FileFormatApiRow>(`/${id}/restore`, { method: 'PATCH' });
